import { handleRef } from '@stardust-ui/react-component-ref';
import classNames from 'classnames/bind';
import Button from 'mcs-toolkit/components/Button';
import InputSemantic from 'mcs-toolkit/components/Input';
import React from 'react';
import { InputProps as SemanticInputProps } from 'semantic-ui-react';
import { copyStringToClipboard } from 'system/libs/copyStringToClipboard';
import { EventHelper } from 'utils/EventHelper';

import IconClose from 'reactComponents/icons/close';
import IconCopy from 'reactComponents/icons/IconCopy';

import styles from './Input.css';

const css = classNames.bind(styles);

export type InputProps = SemanticInputProps & {
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    onFocusCapture?: React.FocusEventHandler<HTMLInputElement>;
    onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    withCopyButton?: boolean;
    clearable?: boolean;
    unit?: string;
    rightIcon?: JSX.Element;
    noSystemSpinner?: boolean;
    autoComplete?: string;
    inputRef?: (node: HTMLInputElement | null) => void;
    leftInnerContent?: JSX.Element;
    rightInnerContent?: JSX.Element;
    dataTest?: string;
    value?: string;
    initialValue?: string;
    style?: React.CSSProperties;
};

type State = {
    value: string;
    isControlled: boolean;
};
export class Input extends React.PureComponent<InputProps, State> {
    _inputRef = React.createRef();

    state: State = {
        value: this.props.value || this.props.initialValue || '',
        isControlled: this.props.value !== undefined,
    };

    static defaultProps = {
        dataTest: 'input',
    };

    get _value() {
        if (this.props.value != null && this.state.isControlled) {
            return this.props.value;
        }
        return this.state.value;
    }

    _handleChange = (e, data, ...args) => {
        if (!this.state.isControlled) {
            this.setState({
                value: data.value,
            });
        }

        if (this.props.onChange) {
            // @ts-ignore
            return this.props.onChange(e, data, ...args);
        }
    };

    _handleClear = (event) => {
        EventHelper.emitInputEvent(this._inputRef.current, '');
        event.stopPropagation();
    };

    _renderLeftInnerContent() {
        return (
            this.props.leftInnerContent && (
                <div className={css('leftInnerContentWrapper')}>
                    {this.props.leftInnerContent}
                </div>
            )
        );
    }

    _renderRightInnerContent() {
        if (this.props.rightInnerContent) {
            return (
                <div className={css('rightInnerContentWrapper')}>
                    {this.props.rightInnerContent}
                </div>
            );
        }
    }

    public setValue = (value: string) => {
        EventHelper.emitInputEvent(this._inputRef.current, value);
    };

    render() {
        let {
            clearable,
            className,
            unit,
            rightIcon,
            noSystemSpinner,
            autoComplete,
            onChange,
            value,
            inputRef,
            initialValue,
            leftInnerContent,
            rightInnerContent,
            withCopyButton,
            style,
            dataTest,
            ...props
        } = this.props;

        if (withCopyButton) {
            rightIcon = (
                <Button
                    basic
                    onClick={() =>
                        copyStringToClipboard(
                            value || '',
                            'Значение скопировано в буффер обмена',
                        )
                    }
                >
                    <IconCopy className={css('copyIcon')} />
                </Button>
            );
        }

        if (!rightIcon && unit) {
            rightIcon = <div className={css('unit')}>{unit}</div>;
        }

        if (clearable && this._value.length) {
            rightIcon = (
                <IconClose
                    className={css('iconClose')}
                    onClick={this._handleClear}
                />
            );
        }

        const hasRightIcon = rightIcon;

        return (
            <InputSemantic
                className={css('root', className, {
                    hasRightIcon,
                    noSystemSpinner,
                    leftInnerContent,
                    rightInnerContent,
                })}
                value={this._value}
                onChange={this._handleChange}
                {...props}
            >
                {
                    // don't change order of iconClose, rightIcon, leftInnerContent, rightInnerContent
                }
                {rightIcon && (
                    <div className={css('rightIcon')}>{rightIcon}</div>
                )}
                <input
                    data-test={dataTest}
                    ref={(node) => {
                        handleRef(this.props.inputRef as any, node);
                        handleRef(this._inputRef, node);
                    }}
                    autoComplete={autoComplete}
                    onBlur={this.props.onBlur}
                    onFocusCapture={this.props.onFocusCapture}
                    onFocus={this.props.onFocus}
                    onKeyPress={this.props.onKeyPress}
                    onKeyDown={this.props.onKeyDown}
                    style={style}
                />
                {this._renderLeftInnerContent()}
                {this._renderRightInnerContent()}
            </InputSemantic>
        );
    }
}
